import React, { useEffect, useMemo, useState } from "react";
import { Button } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV, faSearch } from "@fortawesome/free-solid-svg-icons";
import { ResponsivePie } from "@nivo/pie";
import Select from "react-select";

import Loader from "../../../components/Loader";
import { useNavigate, useParams } from "react-router-dom";
import { ReactStrapModal } from "../../../components/reactstrap_modal/ReactStrapModal";
import useFetch from "../../../components/useFetch";
import { alertContext } from "../../../components/modal/ModalComponent";
import AddExternalModel from "../../hub/AddExternalModel";
import {
  LabelsIcon,
  ModelIcon,
} from "../../Common/SvgIcons/WorkflowIcons";
import ModelVersionsTable from "./ModelVersionsTable";
import ModelVersionsComparison from "./ModelVersionsComparison";
import ModelArchitectureSidebar from "./ModelArchitectureSidebar";
import DateParse from "../../../components/DateParse";
import nivoTheme from "../train/graph/nivoTheme";
import "../data/Dataset.scss";
import "../train/experiment_list/ExperimentList.scss";
import "./Models.scss";

const MAX_COMPARE_VERSIONS = 8;
const PREFERRED_ARCHITECTURE_ORDER = ["yolov5", "yolov7", "yolox"];

export const sortArchitectureKeys = (keys = []) =>
  [...keys].sort((left, right) => {
    const leftIndex = PREFERRED_ARCHITECTURE_ORDER.indexOf(left.toLowerCase());
    const rightIndex = PREFERRED_ARCHITECTURE_ORDER.indexOf(right.toLowerCase());
    if (leftIndex === -1 && rightIndex === -1) return left.localeCompare(right);
    if (leftIndex === -1) return 1;
    if (rightIndex === -1) return -1;
    return leftIndex - rightIndex;
  });

function formatFramework(fw) {
  if (!fw) return "";
  const map = {
    pytorch: "PyTorch",
    pt: "PyTorch",
    tensorflow: "TensorFlow",
    tf: "TensorFlow",
    tflite: "TFLite",
    jax: "JAX",
    onnx: "ONNX",
  };
  const key = String(fw).toLowerCase();
  return map[key] || fw;
}

function formatLabel(value) {
  if (!value) return "";
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatArchitectureName(value) {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "yolox") return "YOLOX";
  if (normalized.startsWith("yolov")) {
    return `YOLOv${normalized.replace("yolov", "")}`;
  }
  return formatLabel(value);
}

function stringToColor(value = "") {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = value.charCodeAt(index) + ((hash << 5) - hash);
  }
  return `hsl(${Math.abs(hash) % 360}, 60%, 55%)`;
}

function formatModelVersion(version) {
  if (version == null || version === "") return "—";
  return `v${String(version).replace(/^v/i, "")}`;
}

export function getArchMeta(archKey, archBucket) {
  const versions = Array.isArray(archBucket?.data) ? archBucket.data : [];
  const first = versions[0] || {};
  const classNames = first?.config?.exp_config?.data?.class_names || [];
  const formats = Array.isArray(first.model_graph_format)
    ? first.model_graph_format
    : [];
  const featureExtractor =
    first?.config?.exp_config?.model?.["Feature Extractor"] ||
    first?.config?.exp_config?.model?.feature_extractor ||
    "";

  const latest = [...versions].sort((a, b) => {
    const aDate = new Date(String(a.updated_on || a.created_on || "").replace(" ", "T"));
    const bDate = new Date(String(b.updated_on || b.created_on || "").replace(" ", "T"));
    return bDate - aDate;
  })[0] || first;

  return {
    key: archKey,
    name: formatArchitectureName(first.model_architecture || archKey),
    architecture: first.model_architecture || archKey,
    framework: formatFramework(first.framework),
    taskType: formatLabel(first.task_type),
    domain: formatLabel(first.model_domain),
    modelClass: formatLabel(first.model_class),
    description: first.model_description || "",
    updatedOn: latest.updated_on || latest.created_on || first.updated_on || first.created_on,
    versionCount: versions.length,
    latestVersion: latest.version || first.version || "",
    benchmarkDataset: first.benchmark_dataset || first?.config?.exp_config?.data?.ds_name || "",
    classCount: Array.isArray(classNames) ? classNames.length : 0,
    formats,
    featureExtractor: formatLabel(featureExtractor),
    platformTrained: Boolean(first.platform_trained),
    ownership: first.ownership || "",
    organization: first.organization || "",
    importSource: first.import_source || "",
  };
}

export default function Models() {
  const { architecture, projectId } = useParams();
  const [open, setOpen] = React.useState(false);
  const [isAddModelOpen, setIsAddModelOpen] = useState(false);
  const [addModelAlert, setAddModelAlert] = useState();
  const [compareMode, setCompareMode] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [selectedVersionIds, setSelectedVersionIds] = useState([]);
  const [compareLimitMessage, setCompareLimitMessage] = useState("");

  const [heighlt, setHeighlt] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModelSearch, setShowModelSearch] = useState(false);
  const [searchVersion, setSearchVersion] = useState('');
  const [objectsVersion, setObjectsVersion] = useState("latest");
  const [objectsSearch, setObjectsSearch] = useState("");


  const url = `models?filters={"project_id":"${projectId}"}&visual_inspection=false`;
  const { loading, data } = useFetch(url);

  const navigate = useNavigate();

  useEffect(() => {
    if (!data || Object.entries(data)?.length === 0) return;

    const modelKeys = sortArchitectureKeys(Object.keys(data));

    // If there is already an architecture in the URL, just sync highlight with it.
    if (architecture && modelKeys.includes(architecture)) {
      setHeighlt(architecture);
      return;
    }

    // No architecture in URL → try sessionStorage first
    const stored = sessionStorage.getItem(`lastSelectedModel_${projectId}`);

    let initialArch = null;

    if (stored && modelKeys.includes(stored)) {
      initialArch = stored;
    } else {
      // Fallback: use the "latest" model (first key)
      initialArch = modelKeys[0];
    }

    // Navigate to the chosen architecture and sync highlight
    // navigate(`/projects/${projectId}/deepdeploy/${initialArch}`, { replace: true });
    setHeighlt(initialArch);
  }, [data, architecture, navigate, projectId]);


  const datacards = useMemo(() => {
    if (!data) return [];
    return sortArchitectureKeys(Object.keys(data)).map((archKey) =>
      getArchMeta(archKey, data[archKey])
    );
  }, [data]);

  const onModelAdded_Removed = () => {
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  };

  const openAddModelDrawer = () => {
    setAddModelAlert();
    setIsAddModelOpen(true);
  };

  const closeAddModelDrawer = () => {
    setAddModelAlert();
    setIsAddModelOpen(false);
  };

  const closeModelSearch = () => {
    setShowModelSearch(false);
    setSearchTerm("");
  };

  const selectedArchitecture = heighlt || architecture;

  useEffect(() => {
    setSelectedVersionIds([]);
    setCompareMode(false);
    setShowComparison(false);
    setCompareLimitMessage("");
    setSearchVersion("");
    setObjectsVersion("latest");
    setObjectsSearch("");
  }, [selectedArchitecture]);

  const cancelCompareSelection = () => {
    setCompareMode(false);
    setShowComparison(false);
    setSelectedVersionIds([]);
    setCompareLimitMessage("");
  };

  const handleCompareClick = () => {
    if (showComparison) {
      // Back to selection while keeping current picks.
      setShowComparison(false);
      setCompareMode(true);
      return;
    }

    if (!compareMode) {
      setCompareMode(true);
      setCompareLimitMessage("");
      return;
    }

    if (selectedVersionIds.length < 2) {
      setCompareLimitMessage(
        `Select 2 or more versions to compare (up to ${MAX_COMPARE_VERSIONS}).`
      );
      return;
    }

    setCompareLimitMessage("");
    setShowComparison(true);
  };

  const handleToggleVersion = (model) => {
    if (!model?._id) return;
    setSelectedVersionIds((prev) => {
      if (prev.includes(model._id)) {
        setCompareLimitMessage("");
        return prev.filter((id) => id !== model._id);
      }
      if (prev.length >= MAX_COMPARE_VERSIONS) {
        setCompareLimitMessage(
          `You can compare up to ${MAX_COMPARE_VERSIONS} versions at once. Deselect another version first.`
        );
        return prev;
      }
      setCompareLimitMessage("");
      return [...prev, model._id];
    });
  };

  const selectedArchData = data?.[selectedArchitecture];
  const canCompareVersions = (selectedArchData?.data?.length ?? 0) >= 2;
  const firstVersion = selectedArchData?.data?.[0];
  const selectedMeta = useMemo(
    () => getArchMeta(selectedArchitecture, selectedArchData),
    [selectedArchitecture, selectedArchData]
  );

  const architectureVersions = useMemo(
    () =>
      [...(selectedArchData?.data || [])].sort((left, right) =>
        String(right.version || "").localeCompare(
          String(left.version || ""),
          undefined,
          { numeric: true, sensitivity: "base" }
        )
      ),
    [selectedArchData]
  );

  const latestArchitectureVersion = architectureVersions[0];
  const objectVersionOptions = latestArchitectureVersion
    ? [
        {
          value: "latest",
          label: `Latest (${formatModelVersion(latestArchitectureVersion.version)})`,
        },
        ...architectureVersions.slice(1).map((model) => ({
          value: String(model._id || model.version),
          label: formatModelVersion(model.version),
        })),
      ]
    : [];

  const selectedObjectsVersion =
    objectsVersion === "latest"
      ? latestArchitectureVersion
      : architectureVersions.find(
          (model) => String(model._id || model.version) === String(objectsVersion)
        ) || latestArchitectureVersion;

  const selectedObjectVersionOption =
    objectVersionOptions.find(
      (option) => String(option.value) === String(objectsVersion)
    ) ||
    objectVersionOptions[0] ||
    null;

  const objectsOfInterest = useMemo(() => {
    const configuredClasses =
      selectedObjectsVersion?.config?.exp_config?.data?.class_names;
    const rawObjects = selectedObjectsVersion?.objects_of_interest;
    const names = Array.isArray(rawObjects)
      ? rawObjects
      : typeof rawObjects === "string"
        ? rawObjects.split(",")
        : Array.isArray(configuredClasses)
          ? configuredClasses
          : [];
    const colorMap =
      selectedObjectsVersion?.config?.exp_config?.data?.viz_info?.idx2color || {};

    return [...new Set(names.map((name) => String(name).trim()).filter(Boolean))].map(
      (name, index) => {
        const rgb = colorMap[index] || colorMap[String(index)];
        const color =
          Array.isArray(rgb) && rgb.length >= 3
            ? `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`
            : stringToColor(name);
        return { name, color };
      }
    );
  }, [selectedObjectsVersion]);

  const filteredObjects = objectsOfInterest.filter((item) =>
    item.name.toLowerCase().includes(objectsSearch.trim().toLowerCase())
  );
  const objectPieData = filteredObjects.map((item) => ({
    id: item.name,
    label: item.name,
    value: 1,
    color: item.color,
  }));

  const selectedCompareModels = useMemo(() => {
    const rows = selectedArchData?.data || [];
    return selectedVersionIds
      .map((id) => rows.find((row) => String(row._id) === String(id)))
      .filter(Boolean);
  }, [selectedArchData, selectedVersionIds]);

  useEffect(() => {
    if (canCompareVersions) return;
    setCompareMode(false);
    setShowComparison(false);
    setSelectedVersionIds([]);
    setCompareLimitMessage("");
    setSearchVersion("");
  }, [canCompareVersions]);

  const filteredArchData = useMemo(() => {
    if (!selectedArchData) return undefined;
    if (!searchVersion.trim()) return selectedArchData;

    const term = searchVersion.toLowerCase();

    const searchableAccessors = (selectedArchData.columns || [])
      .map((column) => column?.accessor)
      .filter(Boolean);

    const filteredRows = (selectedArchData.data || []).filter((model) => {
      const accessors = searchableAccessors.length
        ? searchableAccessors
        : Object.keys(model || {});

      return accessors.some((accessor) => {
        const value = model?.[accessor];
        if (Array.isArray(value)) {
          return value.some((item) => String(item).toLowerCase().includes(term));
        }
        return String(value ?? "").toLowerCase().includes(term);
      });
    });

    return { ...selectedArchData, data: filteredRows };
  }, [selectedArchData, searchVersion]);

  const handleOpenModel = (model) => {
    if (!model?.name) return;
    const modelParams = new URLSearchParams({
      architecture: selectedArchitecture,
      version: String(model.version ?? ""),
    });
    if (model._id) modelParams.set("modelId", model._id);
    navigate(
      `/projects/${projectId}/deepdeploy/${encodeURIComponent(
        selectedArchitecture
      )}/model-info/${encodeURIComponent(model.name)}?${modelParams.toString()}`,
      {
        state: {
          selectedCard: model,
          through_project: true,
          version: model.version,
          projectId,
          architecture: selectedArchitecture,
        },
      }
    );
  };

  return (
    <>
      {(data?.length === 0 || data === undefined) && loading ? (
        <div
          className="d-flex flex-column align-items-center justify-content-center"
          style={{ minHeight: "70vh" }}
        >
          <Loader />
          <h5 className="fw-semibold mb-2">Loading Models...</h5>
          <p className="text-center text-secondary" style={{ maxWidth: "450px" }}>
            Please wait while we get your deployable models into the workspace.
          </p>
        </div>
      ) : (
        <>
          {data && Object.entries(data)?.length > 0 ? (
            <>
              <div className="dataset-container gap-3 gap-md-4 deploy-landing">
                <ModelArchitectureSidebar
                  cards={datacards}
                  selectedArchitecture={selectedArchitecture}
                  searchTerm={searchTerm}
                  onSearchTermChange={setSearchTerm}
                  showSearch={showModelSearch}
                  onShowSearch={() => setShowModelSearch(true)}
                  onCloseSearch={closeModelSearch}
                  onImport={openAddModelDrawer}
                  onSelect={(item) => {
                    if (item.key === heighlt) return;
                    sessionStorage.setItem(`lastSelectedModel_${projectId}`, item.key);
                    setHeighlt(item.key);
                  }}
                />

                <div className={`Dataset-version-container d-flex flex-column  gap-3 gap-md-4`}>


                  <div className="selected-model-container ">
                    <div className="container-card selected-model-card p-3">
                      <div className="selected-model-header">
                        <div className="selected-model-main">
                          <div className="model-icon-lg">
                            <ModelIcon style={{ width: "30px", height: "30px" }} />
                          </div>

                          <div className="selected-model-copy">
                            <h4 className="m-0 second-title text-primary">
                              {(selectedMeta.name || selectedArchitecture || "").replace(/_/g, " ")}
                            </h4>
                            {/* {headerDescription ? (
                              <p className="selected-model-desc mb-0 mt-1 text-secondary">
                                {headerDescription}
                              </p>
                            ) : null} */}
                            <div className="model-meta-owner mt-1">
                              {firstVersion?.organization && (
                                <span className="d-j-a gap-1" style={{ width: "fit-content" }}>
                                  <img src="/ico/user.png" width={15} height={15} alt="" />
                                  <span className="model-meta-owner-text">
                                    {firstVersion.organization}
                                  </span>
                                </span>
                              )}
                            </div>
                            <div className="model-meta-dates">
                              {firstVersion?.created_on && (
                                <span className="d-j-a gap-1" style={{ width: "fit-content" }}>
                                  Created {DateParse(firstVersion.created_on)}
                                </span>
                              )}
                              {selectedMeta.updatedOn && (
                                <>
                                  {firstVersion?.created_on && (
                                    <div className="vertical-divider" style={{ height: "10px" }} />
                                  )}
                                  <span className="d-j-a gap-1" style={{ width: "fit-content" }}>
                                    Modified {DateParse(selectedMeta.updatedOn)}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="selected-model-actions">
                          <button
                            type="button"
                            className="btn button-secondary px-2 d-j-a badge-border"
                            aria-label="More actions"
                          >
                            <FontAwesomeIcon icon={faEllipsisV} />
                          </button>
                        </div>
                      </div>
                    </div>




                  </div>

                  <div className="model-version-content">
                    {compareLimitMessage ? (
                      <div className="model-compare-limit-msg mb-2">
                        {compareLimitMessage}
                      </div>
                    ) : null}
                    {showComparison ? (
                      <div className="model-compare-panel container-card p-3">
                        <ModelVersionsComparison
                          models={selectedCompareModels}
                          onChangeSelection={() => {
                            setShowComparison(false);
                            setCompareMode(true);
                          }}
                        />
                      </div>
                    ) : (
                      data && selectedArchitecture && filteredArchData && (
                        <ModelVersionsTable
                          columns={filteredArchData.columns || []}
                          rows={filteredArchData.data || []}
                          searchValue={searchVersion}
                          onSearchChange={setSearchVersion}
                          compareMode={compareMode}
                          showComparison={showComparison}
                          canCompareVersions={canCompareVersions}
                          selectedVersionIds={selectedVersionIds}
                          onCompare={handleCompareClick}
                          onCancelCompare={cancelCompareSelection}
                          onToggleVersion={handleToggleVersion}
                          onOpenVersion={handleOpenModel}
                        />
                      )
                    )}
                  </div>

                  <section className="container-card model-object-interest">
                    <div className="class-dist-header">
                      <div className="class-dist-header-title">
                        <h6 className="fw-bold d-flex align-items-center gap-2">
                          <LabelsIcon
                            className="wf-icon"
                            style={{ height: "23px", width: "30px", transform: "rotate(90deg)" }}
                          />
                          Objects of Interest
                        </h6>
                        <div className="text-secondary mb-0">
                          Total Objects {objectsOfInterest.length}
                          {selectedObjectsVersion ? (
                            <>
                              {" · "}
                              {objectsVersion === "latest"
                                ? `Latest (${formatModelVersion(selectedObjectsVersion.version)})`
                                : formatModelVersion(selectedObjectsVersion.version)}
                            </>
                          ) : null}
                        </div>
                      </div>

                      <div className="class-dist-header-controls">
                        {architectureVersions.length > 1 ? (
                          <Select
                            className="class-distribution-version-select"
                            classNamePrefix="class-distribution-version"
                            value={selectedObjectVersionOption}
                            options={objectVersionOptions}
                            isSearchable={false}
                            isClearable={false}
                            menuPlacement="auto"
                            onChange={(option) => {
                              setObjectsVersion(option?.value ?? "latest");
                              setObjectsSearch("");
                            }}
                          />
                        ) : null}

                        <div className="d-a badge-border class-dist-search-wrap">
                          <FontAwesomeIcon
                            className="search border-0 text-secondary"
                            icon={faSearch}
                            style={{ position: "relative", opacity: "0.8" }}
                          />
                          <input
                            type="text"
                            placeholder=" Search in objects..."
                            className="search-1 class-dist-search"
                            value={objectsSearch}
                            onChange={(event) => setObjectsSearch(event.target.value)}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="class-dist-container">
                      <div className="class-dist-chart">
                        {objectPieData.length ? (
                          <ResponsivePie
                            data={objectPieData}
                            colors={{ datum: "data.color" }}
                            innerRadius={0.65}
                            enableArcLabels={false}
                            enableArcLinkLabels={false}
                            padAngle={2}
                            cornerRadius={4}
                            theme={nivoTheme}
                            margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
                          />
                        ) : null}
                        <div className="class-dist-chart-center">
                          {filteredObjects.length}
                          <div className="model-object-interest__chart-label">OBJECTS</div>
                        </div>
                      </div>

                      <div
                        className="vertical-divider dataset-buttons-outer class-dist-divider"
                        style={{ height: "200px" }}
                      />

                      <div className="class-dist-right">
                        <div className="model-object-interest__list">
                          {filteredObjects.map((item) => (
                            <div key={item.name} className="class-dist-row pe-0 pe-md-2">
                              <div className="model-object-interest__name">
                                <span
                                  className="model-object-interest__dot"
                                  style={{ backgroundColor: item.color }}
                                />
                                <span title={item.name}>{item.name}</span>
                              </div>
                            </div>
                          ))}
                          {!filteredObjects.length ? (
                            <p className="text-secondary mb-0 py-4 text-center">
                              {objectsSearch.trim()
                                ? `No objects match “${objectsSearch.trim()}”.`
                                : "No objects of interest available for this version."}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </section>
                </div>



              </div>

            </>
          ) : (
            <div
              className="d-flex flex-column align-items-center justify-content-center"
              style={{ minHeight: "70vh" }}
            >
              <div className="analytics-empty-icon mb-3">
                <ModelIcon
                  className="wf-icon text-secondary"
                  style={{ width: 48, height: 48 }}
                />
              </div>

              <h5 className="fw-semibold mb-2">No models found</h5>

              <p className="text-center text-secondary" style={{ maxWidth: "450px" }}>
                This project does not have deployable models yet.
                Import a model to create versions and prepare deployment.
              </p>

              <Button color="primary" onClick={openAddModelDrawer}>
                Import Model
              </Button>
            </div>
          )}

        </>
      )}
      <ReactStrapModal
        open={open}
        close={setOpen}
        path={`forms/data/create_dataset/${projectId}`}
      />
      {isAddModelOpen && (
        <alertContext.Provider value={setAddModelAlert}>
          <AddExternalModel
            path="forms/model_hub/add_to_model_hub"
            useOwnershipAccessToggles
            close={closeAddModelDrawer}
            onModelAdded={onModelAdded_Removed}
            alert={addModelAlert}
            clearAlert={() => setAddModelAlert()}
          />
        </alertContext.Provider>
      )}
    </>
  );
}
